import React, { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Helmet } from "react-helmet"
import NavigationBar from "../../components/ui/NavigationBar"
import Input from "../../components/ui/Input"
import Select from "../../components/ui/Select"
import Button from "../../components/ui/Button"
import Icon from "../../components/AppIcon"
import ProgramCoursesTab from "./components/ProgramCoursesTab"
import LearningPathBuilder from "./components/LearningPathBuilder"
import ProgramCohortsTab from "./components/ProgramCohortsTab"
import ProgramAssessmentsTab from "./components/ProgramAssessmentsTab"
import ProgramLearnersTab from "./components/ProgramLearnersTab"
import ProgramCredentialsTab from "./components/ProgramCredentialsTab"
import ProgramAnalyticsTab from "./components/ProgramAnalyticsTab"
import ComingSoonPanel from "../organization-dashboard/components/ComingSoonPanel"
import { listCourses } from "../../utils/courseHelpers"
import { listPrograms, updateProgram } from "../../utils/programHelpers"

const PROGRAM_TYPE_OPTIONS = [
  { value: "Program", label: "Program" },
  { value: "Bootcamp", label: "Bootcamp" },
  { value: "Academy", label: "Academy" },
  { value: "Certification", label: "Certification" },
  { value: "Workshop", label: "Workshop" },
  { value: "Training Package", label: "Training Package" },
  { value: "Membership", label: "Membership" },
  { value: "Assessment", label: "Assessment" },
  { value: "Educational Resource", label: "Educational Resource" },
]

const TABS = [
  { key: "overview", label: "Overview", icon: "LayoutDashboard" },
  { key: "courses", label: "Courses", icon: "BookOpen" },
  { key: "learningPath", label: "Learning Path", icon: "Route" },
  { key: "cohorts", label: "Cohorts", icon: "UsersRound" },
  { key: "assessments", label: "Assessments", icon: "ClipboardCheck" },
  { key: "learners", label: "Learners", icon: "Users" },
  { key: "credentials", label: "Credentials", icon: "Award" },
  { key: "analytics", label: "Analytics", icon: "BarChart3" },
  { key: "projects", label: "Projects", icon: "FolderKanban" },
  { key: "settings", label: "Settings", icon: "Settings" },
]

// programHelpers.js has no getProgram(orgId, programId) — listPrograms + find
// keeps this page from needing a new Firestore helper for a single fetch.
const OrganizationProgramBuilder = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const orgId = searchParams?.get("orgId")
  const programId = searchParams?.get("programId")

  const [activeTab, setActiveTab] = useState("overview")
  const [program, setProgram] = useState(null)
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState("")

  useEffect(() => {
    const load = async () => {
      if (!orgId || !programId) {
        navigate("/organization-dashboard", { replace: true })
        return
      }
      const [{ programs }, { courses: courseList }] = await Promise.all([listPrograms(orgId), listCourses(orgId)])
      const found = programs?.find((p) => p?.programId === programId)
      if (!found) {
        navigate("/organization-dashboard", { replace: true })
        return
      }
      setProgram(found)
      setCourses(courseList || [])
      setLoading(false)
    }
    load()
  }, [orgId, programId, navigate])

  const handleFieldChange = (field) => (e) => {
    setProgram((prev) => ({ ...prev, [field]: e?.target?.value }))
  }

  // Removing a course from the bundle also drops any Learning Path node that
  // referenced it — a path node for a course no longer in the program is dead weight.
  const handleCourseIdsChange = (nextCourseIds) => {
    setProgram((prev) => ({
      ...prev,
      courseIds: nextCourseIds,
      learningPath: prev?.learningPath?.filter((node) => nextCourseIds?.includes(node?.courseId)) || [],
    }))
  }

  const handleLearningPathChange = (nextPath) => {
    setProgram((prev) => ({ ...prev, learningPath: nextPath }))
  }

  const handleSaveAll = async () => {
    setSaving(true)
    setSaveMessage("")
    const { error } = await updateProgram(orgId, programId, {
      title: program?.title,
      description: program?.description,
      programType: program?.programType,
      status: program?.status,
      deadlineAt: program?.deadlineAt || null,
      courseIds: program?.courseIds || [],
      learningPath: program?.learningPath || [],
    })
    setSaving(false)
    if (error) {
      setSaveMessage("Something went wrong saving. Please try again.")
      return
    }
    setSaveMessage("Saved successfully.")
    setTimeout(() => setSaveMessage(""), 3000)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading program...</span>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>{program?.title || "Program Builder"} - Nesolearn</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <NavigationBar />

        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" iconName="ArrowLeft" iconPosition="left" onClick={() => navigate("/organization-dashboard")}>
              Back to Programs
            </Button>
            {saveMessage && (
              <span className="text-sm text-success flex items-center gap-1">
                <Icon name="CheckCircle2" size={14} />
                {saveMessage}
              </span>
            )}
          </div>

          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground">{program?.title || "Untitled Program"}</h1>
          </div>

          <div className="border-b border-border mb-6 overflow-x-auto">
            <div className="flex gap-1 min-w-max">
              {TABS?.map((tab) => (
                <button
                  key={tab?.key}
                  type="button"
                  onClick={() => setActiveTab(tab?.key)}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab?.key ? "border-accent text-accent" : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon name={tab?.icon} size={14} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === "overview" && (
            <div className="bg-card border border-border rounded-xl p-6 space-y-4">
              <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Program Details</h2>
              <Input label="Title" value={program?.title} onChange={handleFieldChange("title")} />
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Description</label>
                <textarea
                  rows={3}
                  value={program?.description}
                  onChange={handleFieldChange("description")}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">Type</label>
                  <Select options={PROGRAM_TYPE_OPTIONS} value={program?.programType} onChange={(v) => setProgram((p) => ({ ...p, programType: v }))} />
                </div>
                <Input
                  label="Deadline (optional)"
                  type="date"
                  value={program?.deadlineAt ? program.deadlineAt.slice(0, 10) : ""}
                  onChange={(e) => setProgram((p) => ({ ...p, deadlineAt: e?.target?.value ? new Date(e.target.value).toISOString() : null }))}
                />
              </div>
            </div>
          )}

          {activeTab === "courses" && (
            <ProgramCoursesTab courses={courses} selectedCourseIds={program?.courseIds || []} onChange={handleCourseIdsChange} />
          )}

          {activeTab === "learningPath" && (
            <LearningPathBuilder
              path={program?.learningPath || []}
              bundledCourseIds={program?.courseIds || []}
              courses={courses}
              onChange={handleLearningPathChange}
            />
          )}

          {activeTab === "cohorts" && <ProgramCohortsTab orgId={orgId} courseIds={program?.courseIds || []} />}

          {activeTab === "assessments" && <ProgramAssessmentsTab orgId={orgId} courseIds={program?.courseIds || []} />}

          {activeTab === "learners" && <ProgramLearnersTab orgId={orgId} courseIds={program?.courseIds || []} />}

          {activeTab === "credentials" && <ProgramCredentialsTab orgId={orgId} courseIds={program?.courseIds || []} />}

          {activeTab === "analytics" && <ProgramAnalyticsTab orgId={orgId} program={program} courses={courses} />}

          {activeTab === "projects" && <ComingSoonPanel label="Projects" />}

          {activeTab === "settings" && (
            <div className="bg-card border border-border rounded-xl p-6 space-y-4">
              <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Settings</h2>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">Status</p>
                  <p className="text-xs text-muted-foreground">Archived programs are hidden from active listings.</p>
                </div>
                <Select
                  options={[{ value: "active", label: "Active" }, { value: "archived", label: "Archived" }]}
                  value={program?.status || "active"}
                  onChange={(v) => setProgram((p) => ({ ...p, status: v }))}
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <Button variant="outline" iconName="ArrowLeft" iconPosition="left" onClick={() => navigate("/organization-dashboard")}>
              Back to Programs
            </Button>
            <div className="flex items-center gap-3">
              <Button variant="default" loading={saving} onClick={handleSaveAll}>
                Save All Changes
              </Button>
              <Button variant="secondary" iconName="ArrowRight" iconPosition="right" onClick={() => navigate("/organization-dashboard")}>
                Done
              </Button>
            </div>
          </div>
        </main>
      </div>
    </>
  )
}

export default OrganizationProgramBuilder